import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Eye, Loader2, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Chart } from '@/components/Chart';
import { Ellie } from '@/components/Ellie';
import DashboardDetail from '../analysis/DashboardDetail';
import { useTenantUsers } from '@/hooks/useTenantUsers';
import { useScopeCatalog, useUserGroups } from '@/hooks/useUserGroups';
import { useDashboards } from '@/hooks/useDashboards';
import { useAnalyses } from '@/hooks/useAnalyses';
import ApiService from '@/services/api.service';
import { shortid } from '@/services/id.service';
import { FeatureFlag, useFeatureFlag } from '@/services/featureFlag.service';

type SubjectKind = 'user' | 'group';
type View = 'dashboard' | 'chart' | 'ellie';
type DataBranch = 'PROD' | 'DRAFT';

const VIEWS: { value: View; label: string }[] = [
  { value: 'dashboard', label: 'Dashboard' },
  { value: 'chart', label: 'Chart' },
  { value: 'ellie', label: 'Ellie' },
];

/**
 * Queries that don't depend on whose access is being previewed, or on the branch:
 * the preview's own pickers. Kept across a reset so switching subject or branch
 * doesn't blank them -- and the catalog decides the branch, so resetting it would
 * flip the branch back and forth.
 */
const SCOPE_INDEPENDENT_QUERY_KEYS = new Set(['scopeCatalog', 'userGroups', 'tenantUsers']);

function resetScopedQueries(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.resetQueries({
    predicate: (q) => !SCOPE_INDEPENDENT_QUERY_KEYS.has(String(q.queryKey[0])),
  });
}

/**
 * Sends `Plb-View-As: <header>` on every API request while mounted, and returns
 * true once it's in place. Anything React Query cached under another scope (the
 * admin's own, or a previous subject's) is dropped on the way in and on the way
 * out, so no view here or elsewhere in the app ever shows the other scope's data.
 * The content is only rendered once this is true: child queries start before a
 * parent's effects run, and would otherwise go out without the header.
 */
function useViewAs(header: string | null, branch: DataBranch): boolean {
  const queryClient = useQueryClient();
  const key = header ? `${header}@${branch}` : null;
  const [applied, setApplied] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    const api = ApiService.getInstance();
    api.setViewAs(header, branch);
    resetScopedQueries(queryClient);
    setApplied(key);
    return () => {
      api.setViewAs(null);
      resetScopedQueries(queryClient);
    };
  }, [header, branch, key, queryClient]);

  return applied === key;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

/**
 * Security & Governance → Preview: see a dashboard, a chart or Ellie with a
 * user's or group's data access, to check restrictions do what's intended.
 * Selections live in the URL so a preview can be reloaded or shared with another
 * admin.
 */
export default function PreviewTab() {
  const [params, setParams] = useSearchParams();
  const kind: SubjectKind = params.get('kind') === 'group' ? 'group' : 'user';
  const subjectId = params.get('subject') || '';
  const view: View = (VIEWS.find((v) => v.value === params.get('view'))?.value) ?? 'dashboard';
  const targetId = params.get('target') || '';
  // Chosen here, not taken from the global Develop mode: scopes aren't branched, so
  // the question is which model to check them against -- what members see now, or
  // what they'll see once Develop is published.
  const { data: catalog } = useScopeCatalog();
  const hasDraft = !!catalog?.has_draft;
  // From the URL, falling back to Production only once the catalog has loaded and
  // says there's no Develop -- not while it's loading, which would flip the branch.
  const branch: DataBranch = params.get('data') === 'develop' && catalog?.has_draft !== false ? 'DRAFT' : 'PROD';

  const update = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(changes)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setParams(next, { replace: true });
  };

  const { data: users, isLoading: usersLoading } = useTenantUsers();
  const { data: groups, isLoading: groupsLoading } = useUserGroups();
  const { data: dashboards, isLoading: dashboardsLoading } = useDashboards({ pageSize: 500, enabled: view === 'dashboard' });
  const { data: charts, isLoading: chartsLoading } = useAnalyses({
    pageSize: 500,
    filters: { ephemeral: { $ne: true } },
    enabled: view === 'chart',
  });

  const subjectOptions = useMemo(
    () =>
      kind === 'user'
        ? (users ?? []).map((u) => ({ value: u.id, label: u.name || u.email, description: u.name ? u.email : undefined }))
        : (groups ?? []).map((g) => ({ value: g.id, label: g.name, description: g.description || undefined })),
    [kind, users, groups],
  );
  const subjectLabel = subjectOptions.find((o) => o.value === subjectId)?.label;

  const targetOptions = useMemo(
    () =>
      view === 'dashboard'
        ? (dashboards?.records ?? []).map((d) => ({ value: d.id, label: d.name }))
        : (charts?.records ?? []).map((a) => ({ value: a.id, label: a.name || 'Untitled chart' })),
    [view, dashboards, charts],
  );

  const header = subjectId ? `${kind}:${subjectId}` : null;
  const ready = useViewAs(header, branch);

  // A fresh Ellie conversation per subject, so one never continues under another's access.
  const [threadId, setThreadId] = useState(() => shortid());
  useEffect(() => setThreadId(shortid()), [header, branch]);

  const useCasesEnabled = useFeatureFlag(FeatureFlag.ALLOW_USECASE_MANAGEMENT, false);

  let content: ReactNode = null;
  if (!header) {
    content = <Placeholder>Choose a {kind} to preview their access.</Placeholder>;
  } else if (!ready) {
    content = (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  } else if (view === 'ellie') {
    content = (
      <Ellie
        key={`${header}:${branch}:${threadId}`}
        conversationId={threadId}
        agentType={useCasesEnabled ? 'ellie' : 'analyst'}
        showHistory={false}
      />
    );
  } else if (!targetId) {
    content = <Placeholder>Choose a {view} to open.</Placeholder>;
  } else if (view === 'dashboard') {
    content = <DashboardDetail key={`${header}:${branch}:${targetId}`} embeddedDashboardId={targetId} fillParent />;
  } else {
    content = (
      <div className="h-full p-4">
        <Chart key={`${header}:${branch}:${targetId}`} analysisId={targetId} disableSave disableAddToDashboard />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-4">
        <Field label="Preview as">
          <div className="flex gap-2">
            <Select value={kind} onValueChange={(v) => update({ kind: v, subject: null })}>
              <SelectTrigger className="w-28">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="user">User</SelectItem>
                <SelectItem value="group">Group</SelectItem>
              </SelectContent>
            </Select>
            <Combobox
              className="w-72"
              options={subjectOptions}
              value={subjectId}
              onValueChange={(v) => update({ subject: v })}
              placeholder={kind === 'user' ? 'Select a user…' : 'Select a group…'}
              emptyText={kind === 'user' ? 'No users found.' : 'No groups yet.'}
              isLoading={kind === 'user' ? usersLoading : groupsLoading}
            />
          </div>
        </Field>
        <Field label="Data">
          <Select
            value={branch}
            onValueChange={(v) => update({ data: v === 'DRAFT' ? 'develop' : null, target: null })}
            disabled={!hasDraft && branch === 'PROD'}
          >
            <SelectTrigger className="w-64">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="PROD">Production</SelectItem>
              <SelectItem value="DRAFT">Develop (includes unpublished changes)</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Show">
          <div className="flex gap-2">
            <Select value={view} onValueChange={(v) => update({ view: v, target: null })}>
              <SelectTrigger className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {VIEWS.map((v) => (
                  <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {view === 'ellie' ? (
              <Button variant="outline" onClick={() => setThreadId(shortid())} disabled={!header}>
                <RotateCcw className="h-4 w-4 mr-2" />
                New conversation
              </Button>
            ) : (
              <Combobox
                className="w-72"
                options={targetOptions}
                value={targetId}
                onValueChange={(v) => update({ target: v })}
                placeholder={view === 'dashboard' ? 'Select a dashboard…' : 'Select a chart…'}
                emptyText={view === 'dashboard' ? 'No dashboards found.' : 'No charts found.'}
                isLoading={view === 'dashboard' ? dashboardsLoading : chartsLoading}
              />
            )}
          </div>
        </Field>
      </div>

      {header && (
        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
          <Eye className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <p>
              Previewing as <span className="font-medium">{subjectLabel ?? `this ${kind}`}</span>
              {kind === 'group' ? ' (a member with no restrictions of their own)' : ''}. Data on this tab uses their row
              restrictions and metric visibility
              {branch === 'DRAFT' ? ', against Develop: what they\'ll see once it\'s published' : ', against Production'}.
              The rest of the app still uses your own access{branch === 'DRAFT' ? ' and branch' : ''}.
            </p>
            <p className="text-xs mt-1 opacity-80">
              Only data access changes; you keep your own role and permissions. Conversations started here are saved to
              your own Ellie history.
            </p>
          </div>
        </div>
      )}

      <div className="relative h-[calc(100vh-300px)] min-h-[560px] overflow-hidden rounded-md border bg-background">
        {content}
      </div>
    </div>
  );
}

function Placeholder({ children }: { children: ReactNode }) {
  return <div className="h-full flex items-center justify-center text-sm text-muted-foreground">{children}</div>;
}
